import jsPDF from "jspdf";
import "jspdf-autotable";
import { addLogoToPdf } from "../../../ui/utils/PdfUtils";

const BLUE = [173, 216, 230];
const LIGHT_GREY = [245, 245, 245];
const BLACK = [0, 0, 0];

const pdfValue = (value) => {
  if (value === null || value === undefined) {
    return "N/A";
  }

  const text = String(value).trim();
  return text || "N/A";
};

const formatAddress = (address) => {
  const lines = [
    [address?.hospital, address?.ward, address?.building]
      .filter(Boolean)
      .join(", "),
    [
      address?.street,
      address?.floor,
      address?.zipcode,
      address?.city,
      address?.country,
    ]
      .filter(Boolean)
      .join(", "),
  ].filter(Boolean);

  return lines.join("\n") || "N/A";
};

const personRows = (people, emptyLabel) =>
  people.length > 0
    ? people.map((person) => [
        pdfValue(person?.name),
        pdfValue(person?.firstname),
        pdfValue(person?.email),
        pdfValue(person?.phoneNumber),
      ])
    : [
        [
          {
            content: emptyLabel,
            colSpan: 4,
            styles: { halign: "center", textColor: [90, 90, 90] },
          },
        ],
      ];

const sectionHead = (title, columnCount) => [
  {
    content: title,
    colSpan: columnCount,
    styles: {
      fillColor: BLUE,
      fontStyle: "bold",
      halign: "center",
      textColor: BLACK,
    },
  },
];

const labelCell = (content) => ({
  content,
  styles: { fillColor: LIGHT_GREY, fontStyle: "bold" },
});

const getTableY = (pdf, fallback) =>
  (pdf.lastAutoTable?.finalY || pdf.autoTable.previous?.finalY || fallback) + 7;

const commonTableOptions = (leftMargin) => ({
  margin: { left: leftMargin, right: leftMargin },
  theme: "grid",
  styles: {
    cellPadding: 2.5,
    font: "helvetica",
    fontSize: 9,
    lineColor: BLACK,
    lineWidth: 0.2,
    minCellHeight: 9,
    overflow: "linebreak",
    textColor: BLACK,
    valign: "middle",
  },
});

export const printCenterSummary = ({
  center,
  studyCode,
  protocols = [],
  kitAddress = {},
  sampleAddress = {},
  contact = {},
  managers = [],
  monitors = [],
  filename,
}) => {
  if (!center) {
    return;
  }

  const pdf = new jsPDF("p", "mm", "a4");
  const logoHeight = addLogoToPdf(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const leftMargin = 10;
  const contentWidth = pageWidth - leftMargin * 2;
  const resolvedStudyCode = pdfValue(
    studyCode ||
      center.Study?.cdlStudyCode ||
      center.Study?.sponsorStudyCode,
  );
  const centerNumber = pdfValue(center.centerNumber);
  const protocolReferences =
    protocols
      .map((protocol) => protocol?.reference)
      .filter(Boolean)
      .join(", ") || "N/A";
  const status = center.status
    ? `${center.status.charAt(0).toUpperCase()}${center.status.slice(1)}`
    : "N/A";
  const tableOptions = commonTableOptions(leftMargin);
  let y = logoHeight + 30;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(
    `CDL Pharma study code: ${resolvedStudyCode}`,
    pageWidth - leftMargin,
    18,
    { align: "right" },
  );

  pdf.setFillColor(...BLUE);
  pdf.rect(leftMargin, y - 8, contentWidth, 12, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text(
    `CENTER INFORMATION - CENTER ${centerNumber}`,
    pageWidth / 2,
    y - 0.5,
    { align: "center" },
  );
  y += 9;

  pdf.autoTable({
    ...tableOptions,
    startY: y,
    head: [sectionHead("INFO", 4)],
    body: [
      [
        labelCell("Center number"),
        centerNumber,
        labelCell("Office phone"),
        pdfValue(center.officePhone),
      ],
      [
        labelCell("Protocols"),
        protocolReferences,
        labelCell("Status"),
        status,
      ],
    ],
    columnStyles: {
      0: { cellWidth: 31 },
      1: { cellWidth: 64 },
      2: { cellWidth: 31 },
      3: { cellWidth: contentWidth - 126 },
    },
  });

  y = getTableY(pdf, y);
  pdf.autoTable({
    ...tableOptions,
    startY: y,
    head: [sectionHead("ADDRESS", 2)],
    body: [
      [labelCell("Kit order address"), formatAddress(kitAddress)],
      [labelCell("Sample pickup address"), formatAddress(sampleAddress)],
    ],
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: contentWidth - 45 },
    },
  });

  y = getTableY(pdf, y);
  pdf.autoTable({
    ...tableOptions,
    startY: y,
    head: [sectionHead("CONTACT", 4)],
    body: [
      [
        labelCell("Last name"),
        pdfValue(contact.lastName),
        labelCell("First name"),
        pdfValue(contact.firstName),
      ],
      [
        labelCell("Email"),
        pdfValue(contact.contactEmail),
        labelCell("Phone"),
        pdfValue(contact.contactPhone),
      ],
    ],
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 67 },
      2: { cellWidth: 28 },
      3: { cellWidth: contentWidth - 123 },
    },
  });

  const addPeopleTable = (title, people, emptyLabel) => {
    y = getTableY(pdf, y);
    pdf.autoTable({
      ...tableOptions,
      startY: y,
      head: [
        sectionHead(title, 4),
        ["Last name", "First name", "Email", "Phone"],
      ],
      body: personRows(people, emptyLabel),
      headStyles: { fillColor: BLUE, textColor: BLACK, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 37 },
        1: { cellWidth: 37 },
        2: { cellWidth: 70 },
        3: { cellWidth: contentWidth - 144 },
      },
    });
  };

  addPeopleTable("MANAGERS", managers, "No managers");
  addPeopleTable("MONITORS", monitors, "No monitors");

  const pageCount = pdf.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    pdf.setPage(pageNumber);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    pdf.text(
      `Page ${pageNumber} of ${pageCount}`,
      pageWidth - leftMargin,
      pageHeight - 7,
      { align: "right" },
    );
  }

  pdf.save(`${filename}.pdf`);
};
